import { deriveKey, b64, unb64 } from "./secureStore";

export const SYNC_TAG = "HBS1";
const CHUNK = 700;
const ALPHA = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

export const makeCode = () => [...crypto.getRandomValues(new Uint8Array(8))].map((b) => ALPHA[b % ALPHA.length]).join("");
export const showCode = (c) => `${c.slice(0, 4)}-${c.slice(4)}`;
export const PHOTO_FORMAT = "household-budget-sync-photos";
export const cleanCode = (c) => String(c || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

async function pipe(bytes, stream) {
  return new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(stream)).arrayBuffer());
}

// Device data only — receipt photos stay on their device (too large for QR codes)
export const syncPayload = (s) => {
  const { audit, ...rest } = s;
  return { ...rest, audit: (audit || []).slice(0, 200), sentAt: new Date().toISOString() };
};

// Returns base64 text: salt(16) + iv(12) + AES-GCM(deflate(JSON))
export async function sealSync(state, code) {
  const packed = await pipe(new TextEncoder().encode(JSON.stringify(syncPayload(state))), new CompressionStream("deflate-raw"));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await deriveKey(cleanCode(code), salt), packed));
  const out = new Uint8Array(28 + data.length);
  out.set(salt, 0);
  out.set(iv, 16);
  out.set(data, 28);
  return b64(out);
}

export async function openSync(text, code) {
  let raw;
  try { raw = unb64(String(text).replace(/\s+/g, "")); } catch { throw new Error("That sync text is incomplete or damaged."); }
  if (raw.length < 40) throw new Error("That sync text is incomplete or damaged.");
  let plain;
  try {
    plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: raw.slice(16, 28) }, await deriveKey(cleanCode(code), raw.slice(0, 16)), raw.slice(28));
  } catch {
    throw new Error("Wrong sync code, or the sync text is incomplete — check the code and copy the whole text again.");
  }
  return JSON.parse(new TextDecoder().decode(await pipe(new Uint8Array(plain), new DecompressionStream("deflate-raw"))));
}

export function toFrames(text) {
  const id = Math.random().toString(36).slice(2, 7);
  const n = Math.max(1, Math.ceil(text.length / CHUNK));
  return Array.from({ length: n }, (_, i) => `${SYNC_TAG}|${id}|${i}|${n}|${text.slice(i * CHUNK, (i + 1) * CHUNK)}`);
}

export function parseFrame(s) {
  const m = /^HBS1\|(\w+)\|(\d+)\|(\d+)\|(.*)$/s.exec(s || "");
  return m ? { id: m[1], i: Number(m[2]), n: Number(m[3]), chunk: m[4] } : null;
}

const LISTS = ["savedReceiptSearches", "accounts", "members", "categories", "transactions", "statements", "bills", "billMoves", "debts", "goals", "mileage", "tripTemplates", "irregular", "audit"];
const keyOf = (x) => x.id ?? x.key ?? JSON.stringify(x);

// Photos live per device, so keep this device's receipt links on transactions that arrive
const keepLocalReceipts = (local, txs) => {
  const mine = new Map((local.transactions || []).filter((t) => t.receipts?.length).map((t) => [t.id, t.receipts]));
  return txs.map((t) => {
    const r = mine.get(t.id);
    if (!r) return t;
    const ids = new Set((t.receipts || []).map((x) => x.id));
    return { ...t, receipts: [...(t.receipts || []), ...r.filter((x) => !ids.has(x.id))] };
  });
};

// Sender's data replaces this device's, but this device keeps its settings, inbox and receipt photos
export function replaceSync(local, incoming) {
  const { sentAt, ...rest } = incoming;
  return { ...local, ...rest, settings: local.settings, receiptInbox: local.receiptInbox || [], transactions: keepLocalReceipts(local, incoming.transactions || []) };
}

// Union by id; for items on both devices the sender's copy wins
export function mergeSync(local, incoming) {
  const out = { ...local };
  LISTS.forEach((k) => {
    const map = new Map((local[k] || []).map((x) => [keyOf(x), x]));
    (incoming[k] || []).forEach((x) => map.set(keyOf(x), x));
    out[k] = [...map.values()];
  });
  out.transactions = keepLocalReceipts(local, out.transactions);
  // Household notes from both devices are kept
  ["bills", "goals"].forEach((k) => {
    const mine = new Map((local[k] || []).filter((x) => x.thread?.length).map((x) => [x.id, x.thread]));
    out[k] = out[k].map((x) => {
      const t = mine.get(x.id);
      if (!t) return x;
      const ids = new Set((x.thread || []).map((n) => n.id));
      return { ...x, thread: [...(x.thread || []), ...t.filter((n) => !ids.has(n.id))] };
    });
  });
  out.audit = out.audit.sort((a, b) => String(b.ts).localeCompare(String(a.ts))).slice(0, 1000);
  out.rules = [...new Map([...(local.rules || []), ...(incoming.rules || [])].map((r) => [r.key ?? JSON.stringify(r), r])).values()];
  out.vendorLinks = { ...(local.vendorLinks || {}), ...(incoming.vendorLinks || {}) };
  out.budget = { ...(local.budget || {}), ...(incoming.budget || {}), limits: { ...(local.budget?.limits || {}), ...(incoming.budget?.limits || {}) } };
  ["dismissedBills", "dismissedAlerts", "dismissedReminders", "dismissedTripSuggestions", "receiptAlertDismissed"].forEach((k) => (out[k] = [...new Set([...(local[k] || []), ...(incoming[k] || [])].map((x) => (typeof x === "string" ? x : JSON.stringify(x))))].map((x) => { try { return x.startsWith("{") ? JSON.parse(x) : x; } catch { return x; } })));
  out.taxChecklist = { ...(local.taxChecklist || {}), ...(incoming.taxChecklist || {}) };
  out.setupDone = local.setupDone || incoming.setupDone;
  return out;
}

const LAST = "hb-last-sync";
const PHOTO_SENT = "hb-photo-sent";
const SNOOZE = "hb-sync-snooze";
const WEEK = 7 * 86400000;
export const recordSync = () => localStorage.setItem(LAST, new Date().toISOString());
export const lastSync = () => localStorage.getItem(LAST);
export const lastPhotoSend = () => localStorage.getItem(PHOTO_SENT) || "";
export const recordPhotoSend = (iso) => localStorage.setItem(PHOTO_SENT, iso);
export const snoozeSync = () => localStorage.setItem(SNOOZE, String(Date.now() + WEEK));
// Only nudges devices that have synced before, so single-device households aren't nagged
export const syncDue = (now = Date.now()) => {
  const last = lastSync();
  return !!last && now - new Date(last).getTime() > WEEK && Number(localStorage.getItem(SNOOZE) || 0) < now;
};
